'use client';

import { useTranslations } from 'next-intl';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import clsx from 'clsx';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import ModalTitle from '@/features/user-center/components/modal-title';
import '@/features/user-center/styles/btn.css';

export interface ConfirmDialogProps {
  /** 弹窗标题，不传则使用默认文案 'common.tips' */
  title?: string;
  /** 弹窗内容 */
  content?: string;
  /** 取消按钮文案，不传则使用默认文案 'personal.common.cancel' */
  cancelText?: string;
  /** 确认按钮文案，不传则使用默认文案 'personal.common.confirm' */
  confirmText?: string;
  /** 是否隐藏取消按钮，默认 false */
  hideCancelButton?: boolean;
  /** 是否作为 modal 模式，false 时不会阻止背景交互和 focus trap，默认 true */
  modal?: boolean;
  /** 自定义样式 */
  className?: string;
  /** 取消按钮的回调函数 */
  onCancel?: () => void;
  /** 确定按钮的回调函数 */
  onConfirm?: () => void;
}

// Presentational Component: 纯 UI 展示
interface ConfirmDialogContentProps {
  title: string;
  content?: string;
  cancelText: string;
  confirmText: string;
  hideCancelButton: boolean;
  className?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmDialogContent = ({
  title,
  content,
  cancelText,
  confirmText,
  hideCancelButton,
  className,
  onCancel,
  onConfirm,
}: ConfirmDialogContentProps) => {
  return (
    <DrawerContent
      className={clsx('bg-surface', className)}
    >
      <ModalTitle
        className="pb-0"
        title={title}
        showBack={false}
        onClose={onCancel}
        showClose={true}
      />
      <div className="flex w-full flex-col items-center px-4 py-5">
        {content && (
          <div className="mb-8 flex min-h-[5.8125rem] w-full px-4 items-center justify-center bg-dark-special text-xs font-medium leading-[.9375rem] text-secondary rounded-lg text-center">
            {content}
          </div>
        )}
        <div className="flex w-full items-center">
          {!hideCancelButton && (
            <Button
              onClick={onCancel}
              className="info-btn large-btn mr-2.5 flex-1"
            >
              {cancelText}
            </Button>
          )}
          <Button
            onClick={onConfirm}
            className={clsx(
              'primary-btn large-btn',
              hideCancelButton ? 'w-full' : 'flex-1'
            )}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </DrawerContent>
  );
};

// Container Component: 处理逻辑和状态
const ConfirmDialog = NiceModal.create<ConfirmDialogProps>(
  ({
    title,
    content,
    cancelText,
    confirmText,
    hideCancelButton = false,
    modal: isModal = true,
    className,
    onCancel,
    onConfirm,
  }) => {
    const modal = useModal();
    const tCommon = useTranslations('common');
    const tPersonal = useTranslations('personal');

    const defaultTitle = title ?? tCommon('tips');
    const defaultCancelText = cancelText ?? tPersonal('common.cancel');
    const defaultConfirmText = confirmText ?? tPersonal('common.confirm');

    const handleCancel = () => {
      onCancel?.();
      modal.resolve({ confirmed: false });
      modal.hide();
    };

    const handleConfirm = () => {
      onConfirm?.();
      modal.resolve({ confirmed: true });
      modal.hide();
    };

    const handleOpenChange = (open: boolean) => {
      if (!open) {
        handleCancel();
      }
    };

    return (
      <Drawer open={modal.visible} modal={isModal} onOpenChange={handleOpenChange}>
        <DrawerTitle></DrawerTitle>
        <ConfirmDialogContent
          title={defaultTitle}
          content={content}
          cancelText={defaultCancelText}
          confirmText={defaultConfirmText}
          hideCancelButton={hideCancelButton}
          className={className}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        />
      </Drawer>
    );
  }
);

ConfirmDialog.displayName = 'ConfirmDialog';

// 导出便捷的调用函数
export const showConfirmDialog = (
  props: ConfirmDialogProps = {}
): Promise<{ confirmed: boolean; }> => {
  return NiceModal.show(ConfirmDialog, props);
};

export default ConfirmDialog;

